/**
 * ⟦A-145 · C20⟧ `BoiteDialogue` : la boîte JRPG du bas, cœur de la direction E (« la
 * conversation »), décision de Mathieu du 11/09/2026 après quatre maquettes, verbatim en tête
 * de `docs/chantiers/C20-la-conversation.md`. Elle porte l'hôte (Matt, portrait gauche, §1 bis)
 * et, dès qu'il existe, le joueur (portrait droit) : une conversation entre les deux, pas un
 * simple texte au-dessus d'un formulaire. Ce composant portera ensuite le tutoriel, les leçons
 * et les événements (§1 du chantier) : le slot `enfants` existe déjà pour ça, vide aujourd'hui.
 *
 * Porté depuis `assets/making-of/2026-09-11-DA-creation/E/_commun.css` (système C : bandeau
 * `.boite-ruban`, bordure en escalier `.boite-outer`/`.boite-inner` en deux couches `clip-path`,
 * fond tramé `repeating-conic-gradient`), recopié tel quel dans `app.css`, pas réinventé.
 *
 * **Machine à écrire** (environ 30 caractères par seconde) : le texte complet est présent dans
 * le DOM dès le montage, chaque caractère est un `<span>`, et seule son OPACITÉ bascule au fil
 * du temps (`opacity`, jamais `visibility`/`display`, qui sortiraient le caractère de l'arbre
 * d'accessibilité) : un lecteur d'écran lit donc la réplique entière immédiatement, l'animation
 * n'étant que visuelle, exactement ce que demande le chantier. Un clic/tap sur la boîte affiche
 * tout d'un coup. `prefers-reduced-motion: reduce` saute l'animation d'emblée. Le curseur ▶
 * clignotant (`.curseur-suite`, un triangle CSS, VT323 ne couvre pas le glyphe ▶) n'apparaît
 * qu'une fois la réplique entièrement affichée, comme indicateur de fin.
 *
 * ⟦C27 · A-153⟧ **La voix** (`audio`, prop optionnelle) : voir `BoiteDialogueAudio` plus bas.
 * Absent, le cas de tous les écrans existants, aucun changement de comportement.
 *
 * ⟦C29 · lot D · point 7⟧ **Revu en profondeur** (retour de Matt du 14/09, « c'est la personne
 * qui décide de faire avancer chaque texte, et pas tout le texte est lancé comme ça ») : avant ce
 * chantier, un guetteur faisait avancer `texte` TOUT SEUL dès que la voix franchissait un repère
 * (`onSuite` appelé depuis la boucle audio, jamais depuis un clic) — la voix menait, le joueur
 * subissait. Désormais c'est TOUJOURS un clic (ou Entrée/Espace/tap) qui fait avancer, la voix ne
 * fait plus que jouer SA réplique puis s'arrêter, et sans elle (coupée ou bloquée par le
 * navigateur) la machine à écrire retombe sur son rythme lisible fixe, jamais celui,
 * potentiellement illisible, d'une réplique qu'on n'entend pas.
 *
 * ⟦C42 · A-165 §1⟧ **Un fichier audio PAR réplique, plus de découpe à la lecture.** Retour de
 * Matt du 18/09/2026, verbatim : *« Revérifie les sons d'intro, il y a encore le premier mot de
 * la phrase d'après. J'en ai marre d'entendre le premier mot de la phrase d'après dans la
 * voix. »* Avant ce chantier, `audio` portait UN SEUL fichier pour toute la conversation
 * (`reperes: number[]`) et la boîte devait s'arrêter elle-même au bon endroit — par un guetteur
 * `requestAnimationFrame` qui lisait `el.currentTime` en continu, secondé par un `setTimeout` de
 * secours. Mesuré (`docs/chantiers/C42-un-fichier-par-replique.md`) : cette LECTURE pouvait
 * s'arrêter en retard par rapport au repère visé, et au-delà d'environ 40 ms de retard (la marge
 * d'attaque de la réplique suivante), on entend son premier mot — alors même que les fichiers
 * sources, eux, étaient propres. Le correctif retire la classe de bug entière plutôt que de la
 * rattraper : `audio.src` ne pointe plus jamais que sur LA réplique affichée (un fichier séparé
 * par réplique, produit par le studio), la boîte le joue de son tout début à sa fin NATURELLE
 * (l'événement du navigateur, jamais un repère calculé ni un minuteur) — il n'y a plus de
 * réplique suivante DANS CE FICHIER pour qu'un retard d'arrêt la fasse entendre. Plus de
 * guetteur `requestAnimationFrame`, plus de minuteur de secours, plus de fondu de bordure : ce
 * mécanisme entier existait pour gérer une DÉCOUPE au sein d'un fichier commun, qui n'existe
 * plus. Seul consommateur de `audio` : `screens/Accueil/Accueil.tsx` (la boîte de Matt en jeu —
 * `Studio.tsx`, `Atelier.tsx`, `NouvelleRun.tsx` — ne le passe jamais, donc rien de ce qui suit
 * ne change son comportement).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import { useUIStore } from "../stores/uiStore";

const CARACTERES_PAR_SECONDE = 30;

function mouvementReduit(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export interface Interlocuteur {
  nom: string;
  /** Chemin de l'image ; absente ou en échec de chargement → repli « initiale sur pastille »,
   *  même idée de repli qu'`IconeAsset` (essayer, puis se rabattre au premier `onError`). */
  portrait?: string;
}

function PortraitInterlocuteur({ nom, portrait }: Interlocuteur) {
  const [enErreur, setEnErreur] = useState(false);
  const repli = !portrait || enErreur;
  return (
    <div className="plaque-interlocuteur">
      <div className={`portrait-cadre${repli ? " placeholder" : ""}`}>
        {repli ? (
          <span className="portrait-initiale" aria-hidden="true">
            {nom.trim().charAt(0).toUpperCase() || "?"}
          </span>
        ) : (
          <img src={portrait} alt="" className="pixel-asset" onError={() => setEnErreur(true)} />
        )}
      </div>
      <span className="plaque-nom">{nom}</span>
    </div>
  );
}

/**
 * ⟦C27 · A-153 · révisé ⟦C42 · A-165 §1⟧⟧ La voix qui pilote la boîte
 * (`docs/chantiers/C27-la-voix-de-matt.md` §2, `docs/chantiers/C42-un-fichier-par-replique.md`) :
 * le fichier de LA réplique actuellement affichée (`texte`), et rien d'autre. L'appelant change
 * `src` en même temps que `texte` (le même geste : avancer d'une réplique) ; la boîte le joue du
 * tout début à SA fin naturelle, jamais au-delà, par construction (il n'y a rien d'autre dans ce
 * fichier).
 */
export interface BoiteDialogueAudio {
  src: string;
  /**
   * Sa durée en secondes, si l'appelant la connaît déjà (préchargée avant le montage de la
   * boîte, `Accueil.tsx`) : cale le débit de la machine à écrire sur cette réplique SANS
   * attendre que l'élément `<audio>` local résolve ses propres métadonnées à chaque nouvelle
   * réplique — un aller-retour de décodage qui n'a pas de raison d'être fini au moment où le
   * texte commence à s'afficher. `null`/absente : repli sur le rythme fixe (voir
   * `debitMachineAEcrire`), jamais un pari sur une valeur pas encore fiable.
   */
  duree?: number | null;
}

/**
 * ⟦C42 · A-165 §1⟧ Le débit (caractères par seconde) de la machine à écrire pour `total`
 * caractères : calé sur `duree` secondes de voix si elle est connue, sinon le rythme fixe par
 * défaut. Remplace `dureeDeLaReplique` (⟦C29⟧, qui soustrayait le repère suivant) : avec un
 * fichier par réplique, il n'y a plus de repère suivant à soustraire, sa durée ENTIÈRE est le
 * temps alloué. Plancher à 0,2 s : une durée mal mesurée ne doit jamais figer le texte ni
 * l'afficher instantanément d'un coup, juste un peu vite.
 */
export function debitMachineAEcrire(total: number, duree: number | null | undefined): number {
  if (!duree) return CARACTERES_PAR_SECONDE;
  return total / Math.max(0.2, duree);
}

interface BoiteDialogueProps {
  hote: Interlocuteur;
  joueur?: Interlocuteur;
  texte: string;
  enfants?: ReactNode;
  /**
   * ⟦C21 · A-146 §2⟧ Appelé au clic/Entrée/Espace **quand la réplique est déjà entièrement
   * affichée**, fait avancer la conversation d'un cran (la chambre l'utilise pour chaîner les
   * répliques de Matt et, sur la dernière, refermer la boîte). Omis (le cas d'avant ce
   * chantier, `NouvelleRun.tsx`) : un clic ne fait qu'accélérer l'affichage, comme toujours,
   * état neutre par défaut.
   *
   * ⟦C29 · lot D · 7b⟧ Avec `audio` : n'est plus JAMAIS appelé automatiquement par la voix (voir
   * le commentaire de tête). Le contrat ne change pas pour l'appelant : un clic de plus, il
   * incrémente son index et redonne la réplique suivante, exactement comme sans audio.
   */
  onSuite?(): void;
  /**
   * ⟦C21 · A-146 §2.1⟧ Un lien discret dans le bandeau, à droite du nom de l'hôte, sert à
   * « Passer » une leçon bloquante. Omis par défaut : rien de plus dans le bandeau.
   */
  actionRuban?: { label: string; onClick(): void };
  /**
   * ⟦C22 · A-151⟧ Saute la machine à écrire, le texte est affiché entier dès le montage,
   * comme `prefers-reduced-motion`, mais décidé par l'appelant plutôt que lu sur l'OS. Sert à
   * `screens/Intro/Intro.tsx` : la réplique d'accueil y est déjà cadrée par le minutage propre
   * de l'intro (fondu, 450 ms), un TROISIÈME tempo (le défilement lettre à lettre) par-dessus
   * casserait ce minutage ET recommencerait de zéro à la microseconde où la vraie chambre
   * prend le relais avec sa PROPRE boîte (`useMattChambre`), qui retape, elle, la même réplique
   * au clavier normalement. Omis par défaut (`false`) : aucun changement pour les écrans
   * existants.
   */
  instantane?: boolean;
  /**
   * ⟦C27 · A-153 · révisé ⟦C42 · A-165 §1⟧⟧ La voix qui pilote le RYTHME de la boîte : un
   * fichier PAR réplique (voir `BoiteDialogueAudio`), joué de son tout début à sa fin naturelle
   * dès que `texte` change. Coupé dans les Réglages (`settings.mute`), ou lecture refusée par le
   * navigateur : exactement comme si `audio` était absent pour le rythme, aucun troisième
   * chemin.
   *
   * Ce n'est jamais elle qui fait avancer `texte` (voir le commentaire de tête) : SEUL un clic
   * du joueur fait passer à la réplique suivante, jamais d'avance automatique, même après une
   * longue attente sans qu'il ne se passe plus rien à l'écran (le fichier de la réplique
   * affichée a fini de jouer, silence, rien d'autre ne se déclenche tant que le joueur ne clique
   * pas).
   */
  audio?: BoiteDialogueAudio;
  /**
   * ⟦C29 · lot D · 7a⟧ Coupe la voix immédiatement quand elle passe à `true` — sert à l'appelant
   * qui saute la conversation par un autre geste que `onSuite` (« Passer », un tap long, Échap :
   * `Accueil.tsx`), qui sinon laisserait la réplique en cours continuer de jouer pendant toute
   * l'animation de sortie. Omis par défaut : aucun changement pour les écrans existants.
   */
  interrompreAudio?: boolean;
}

export function BoiteDialogue({
  hote,
  joueur,
  texte,
  enfants,
  onSuite,
  actionRuban,
  instantane,
  audio,
  interrompreAudio,
}: BoiteDialogueProps) {
  const muet = useUIStore((s) => s.settings.mute);
  const audioActif = !!audio && !muet;

  const caracteres = useMemo(() => Array.from(texte), [texte]);
  const [revele, setRevele] = useState(() => (instantane || mouvementReduit() ? caracteres.length : 0));
  const frame = useRef<number | null>(null);

  // ⟦C27 · A-153⟧ L'élément audio (un vrai `<audio>` du DOM, jamais un `new Audio()` détaché :
  // ça le rend observable, `currentTime`/`paused`, depuis un script de vérification headless,
  // exactement ce que demande le chantier).
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  // Le dernier `.play()` a-t-il réellement démarré la lecture (résolu) ou a-t-il été refusé par
  // le navigateur (politique d'autoplay) ? Optimiste par défaut (`true`) : la 1re réplique ne
  // peut pas encore le savoir (la promesse de `.play()` ne s'est pas encore résolue au moment où
  // la machine à écrire calcule son rythme, voir plus bas). ⟦C42 · A-165 §1⟧ Chaque réplique
  // relance désormais son propre `.play()` (son propre fichier) : une coupure se corrige donc
  // d'elle-même dès que l'utilisateur a interagi une fois (l'autoplay redevient autorisé), pas
  // seulement détectée une fois pour toute la conversation comme avant ce chantier.
  const sonConfirmeRef = useRef(true);

  // `onSuite` change d'identité à quasi CHAQUE rendu de l'appelant (une fonction normale, pas
  // mémoïsée côté `useMattChambre`/`Accueil`) : lue via une ref dans les callbacks ci-dessous,
  // pour ne jamais redémarrer une boucle à chaque frappe, même patron que
  // `screens/Intro/Intro.tsx`.
  const onSuiteRef = useRef(onSuite);
  onSuiteRef.current = onSuite;

  // Coupe la voix si la boîte disparaît en cours de route (retrait de l'accueil, navigation),
  // un `<audio>` retiré du DOM ne s'arrête pas toujours net de lui-même selon le navigateur.
  useEffect(() => {
    return () => {
      audioElRef.current?.pause();
    };
  }, []);

  // ⟦C29 · lot D · 7a⟧ Coupe la voix tout de suite quand l'appelant le demande (« Passer », un
  // tap long ou Échap sur `Accueil.tsx`, qui sautent la conversation SANS passer par un clic) :
  // sans ça, la réplique en cours continuerait de jouer pendant toute l'animation de sortie.
  useEffect(() => {
    if (interrompreAudio) {
      audioElRef.current?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interrompreAudio]);

  useEffect(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);

    if (instantane || mouvementReduit()) {
      setRevele(caracteres.length);
      return;
    }

    setRevele(0);
    const total = caracteres.length;

    // ⟦C42 · A-165 §1⟧ Ce changement de `texte` signale toujours un clic du joueur qui vient de
    // faire avancer (ou le montage de la toute première réplique) : dans les deux cas, `audio`
    // pointe désormais sur LE fichier de CETTE réplique, qu'on (re)démarre depuis son tout
    // début — jamais une reprise en cours de route (chaque fichier est indépendant, `el.src`
    // vient de changer via la prop `audio.src` sur l'élément `<audio>` du rendu, ce qui remet sa
    // position de lecture à zéro par construction du navigateur). Rien de tout ceci sans
    // `audio` (la boîte de Matt en jeu) : la branche entière est ignorée, comportement inchangé
    // depuis ⟦C21⟧.
    let caracteresParSeconde = CARACTERES_PAR_SECONDE;
    if (audioActif && audio) {
      const el = audioElRef.current;
      if (el) {
        el.currentTime = 0;
        el.volume = 1;
        void el
          .play()
          .then(() => {
            sonConfirmeRef.current = true;
          })
          .catch(() => {
            // Lecture bloquée (politique d'autoplay) : la boîte reste utilisable, juste muette,
            // jamais une erreur qui casse l'écran (même philosophie que les SFX, §7.6). La
            // machine à écrire de la PROCHAINE réplique retombera sur le rythme lisible fixe,
            // voir le commentaire de `sonConfirmeRef`.
            sonConfirmeRef.current = false;
          });
      }
      // Sans confirmation que la voix joue réellement (coupée dans les Réglages, ou lecture
      // refusée) : rythme lisible fixe, jamais celui, potentiellement illisible, d'une réplique
      // qu'on n'entend pas.
      if (sonConfirmeRef.current) {
        caracteresParSeconde = debitMachineAEcrire(total, audio.duree);
      }
    }

    const debut = performance.now();
    const tick = (maintenant: number) => {
      const ecoule = (maintenant - debut) / 1000;
      const n = Math.min(total, Math.floor(ecoule * caracteresParSeconde));
      setRevele(n);
      if (n < total) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texte]);

  function afficherTout() {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    setRevele(caracteres.length);
  }

  const texteRevele = revele >= caracteres.length;
  // ⟦C29 · lot D · 7b/7c⟧ Prêt à avancer dès que le texte est entièrement affiché — avec ou sans
  // voix, désormais le même critère (avant ce chantier, avec la voix, il fallait EN PLUS attendre
  // la toute fin du fichier sur la dernière réplique : exactement l'avance non désirée par le
  // joueur que ce chantier corrige, voir le commentaire de tête).
  const termine = texteRevele;

  /** Un clic (souris, Entrée, Espace). ⟦C29 · lot D · 7b⟧ Le joueur fait TOUJOURS avancer
   *  lui-même, jamais l'audio ni un minuteur, même après une longue attente : un clic pendant
   *  l'écriture affiche la réplique entière SANS toucher au son, qui continue de jouer sa
   *  réplique jusqu'à SA fin naturelle ; le clic suivant (texte déjà entier) fait avancer pour de
   *  bon et coupe net ce qu'il resterait de cette réplique (`pause()` avant même `onSuite`,
   *  jamais de fondu ici : une interruption VOULUE par le joueur, pas une fin naturelle). Sans
   *  audio (absent, ou coupé dans les Réglages) : `audioElRef.current` est `null`, `?.pause()` ne
   *  fait rien, comportement inchangé depuis ⟦C21⟧. */
  function auGeste() {
    if (!termine) {
      afficherTout();
      return;
    }
    audioElRef.current?.pause();
    onSuite?.();
  }

  function auClavier(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      auGeste();
    }
  }

  return (
    <div className="zone-boite">
      {audio ? (
        <audio ref={audioElRef} src={audio.src} preload="auto" aria-hidden="true" style={{ display: "none" }} />
      ) : null}
      <div
        className="boite-outer"
        onClick={auGeste}
        // ⟦C21 · A-146 §2 · critère 6⟧ ▶ au clavier : la boîte elle-même est le contrôle (pas
        // de bouton séparé à cet effet), donc `role="button"` + `tabIndex` + `onKeyDown`
        // plutôt que de compter sur la seule souris.
        role="button"
        tabIndex={0}
        aria-label={termine ? "Continuer" : "Afficher la réplique en entier"}
        onKeyDown={auClavier}
      >
        <div className="boite-inner">
          <div className="boite-ruban">
            <span className="ruban-marque" aria-hidden="true" />
            <span>{hote.nom}</span>
            {actionRuban ? (
              <button
                type="button"
                className="boite-ruban-action"
                onClick={(e) => {
                  e.stopPropagation();
                  actionRuban.onClick();
                }}
              >
                {actionRuban.label}
              </button>
            ) : null}
          </div>
          <div className="boite-corps" aria-live="polite">
            <PortraitInterlocuteur nom={hote.nom} portrait={hote.portrait} />
            <p className="boite-texte">
              {caracteres.map((car, i) => (
                <span key={i} className={i < revele ? undefined : "boite-car-a-venir"}>
                  {car}
                </span>
              ))}
              {termine ? <span className="curseur-suite" aria-hidden="true" /> : null}
            </p>
            {joueur ? <PortraitInterlocuteur nom={joueur.nom} portrait={joueur.portrait} /> : null}
          </div>
          {enfants}
        </div>
      </div>
    </div>
  );
}
